import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { Supplier, SUPPLIER_CATEGORIES } from '../helpers/suppliersHelpers';
import ImageUploader from './ImageUploader';

interface SupplierFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (supplierData: Omit<Supplier, 'id'>) => void;
  initialSupplier?: Supplier | null;
}

const SupplierForm: React.FC<SupplierFormProps> = ({
  isOpen,
  onClose,
  onSave,
  initialSupplier,
}) => {
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [isVerified, setIsVerified] = useState(true);
  const [rating, setRating] = useState(5.0);
  const [enviosCount, setEnviosCount] = useState(100);
  const [produtosCount, setProdutosCount] = useState(50);
  const [descriptionShort, setDescriptionShort] = useState('');
  const [descriptionFull, setDescriptionFull] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [hospedarPassos, setHospedarPassos] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [hasPrivateLabel, setHasPrivateLabel] = useState(false);
  const [tempoMedioEntrega, setTempoMedioEntrega] = useState('');
  const [whatsapp, setWhatsapp] = useState('');

  // Errors state
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialSupplier) {
      setName(initialSupplier.name);
      setLogoUrl(initialSupplier.logoUrl);
      setBannerUrl(initialSupplier.bannerUrl);
      setIsVerified(initialSupplier.isVerified);
      setRating(initialSupplier.rating);
      setEnviosCount(initialSupplier.enviosCount);
      setProdutosCount(initialSupplier.produtosCount);
      setDescriptionShort(initialSupplier.descriptionShort);
      setDescriptionFull(initialSupplier.descriptionFull);
      setRedirectUrl(initialSupplier.redirectUrl);
      setHospedarPassos(initialSupplier.hospedarPassos);
      setSelectedCategories(initialSupplier.categories || []);
      setHasPrivateLabel(initialSupplier.has_private_label || false);
      setTempoMedioEntrega(initialSupplier.tempo_medio_entrega || '');
      setWhatsapp(initialSupplier.whatsapp || '');
    } else {
      setName('');
      setLogoUrl('');
      setBannerUrl('');
      setIsVerified(true);
      setRating(5.0);
      setEnviosCount(100);
      setProdutosCount(50);
      setDescriptionShort('');
      setDescriptionFull('');
      setRedirectUrl('');
      setHospedarPassos('');
      setSelectedCategories([]);
      setHasPrivateLabel(false);
      setTempoMedioEntrega('');
      setWhatsapp('');
    }
    setErrors({});
  }, [initialSupplier, isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Nome do fornecedor é obrigatório';
    if (!logoUrl.trim()) newErrors.logoUrl = 'URL da Logo é obrigatória';
    if (!bannerUrl.trim()) newErrors.bannerUrl = 'URL do Banner é obrigatória';
    if (!descriptionShort.trim()) newErrors.descriptionShort = 'Descrição curta é obrigatória';
    if (!descriptionFull.trim()) newErrors.descriptionFull = 'Descrição completa é obrigatória';
    if (!redirectUrl.trim()) newErrors.redirectUrl = 'Link do redirecionamento é obrigatório';
    if (selectedCategories.length === 0) newErrors.categories = 'Selecione pelo menos uma categoria';
    if (!tempoMedioEntrega.trim()) newErrors.tempoMedioEntrega = 'Tempo médio de entrega é obrigatório';
    if (!whatsapp.trim()) newErrors.whatsapp = 'WhatsApp do fornecedor é obrigatório';
    
    if (rating < 0 || rating > 5) newErrors.rating = 'A nota deve ser entre 0 e 5';
    if (enviosCount < 0) newErrors.enviosCount = 'O número de envios não pode ser negativo';
    if (produtosCount < 0) newErrors.produtosCount = 'O número de produtos não pode ser negativo';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCategoryToggle = (category: string) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter((c) => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    onSave({
      name,
      logoUrl,
      bannerUrl,
      isVerified,
      rating: Number(rating),
      enviosCount: Number(enviosCount),
      produtosCount: Number(produtosCount),
      descriptionShort,
      descriptionFull,
      redirectUrl,
      hospedarPassos,
      categories: selectedCategories,
      has_private_label: hasPrivateLabel,
      tempo_medio_entrega: tempoMedioEntrega,
      whatsapp,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-2xl bg-[#0f1114] border border-gray-800 rounded-2xl shadow-2xl overflow-hidden my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white">
            {initialSupplier ? 'Editar Fornecedor' : 'Adicionar Novo Fornecedor'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 bg-gray-900/80 hover:bg-gray-800 text-gray-400 hover:text-white rounded-lg border border-gray-800 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
          {/* Row 1: Name & Verified */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Nome do Fornecedor *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: China Divisão 1"
                className={`w-full px-4 py-3 bg-[#16191d] border ${
                  errors.name ? 'border-red-500' : 'border-gray-800 focus:border-[#0FFF55]'
                } rounded-xl text-white text-sm outline-none transition-colors`}
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>

            <div className="flex flex-col justify-center">
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Selo de Verificado
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isVerified}
                  onChange={(e) => setIsVerified(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-400 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0FFF55] peer-checked:after:bg-black peer-checked:after:border-transparent"></div>
                <span className="ml-3 text-sm text-gray-300">Ativado</span>
              </label>
            </div>
          </div>

          {/* Row 2: Categories selection */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Categorias Principais *
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-4 bg-[#16191d] border border-gray-800 rounded-xl max-h-48 overflow-y-auto custom-scrollbar">
              {SUPPLIER_CATEGORIES.map((cat) => {
                const isSelected = selectedCategories.includes(cat);
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => handleCategoryToggle(cat)}
                    className={`flex items-center justify-between p-2.5 rounded-lg border text-left text-xs transition-all ${
                      isSelected
                        ? 'bg-[#0FFF55]/10 border-[#0FFF55] text-white font-semibold'
                        : 'bg-transparent border-gray-800 hover:border-gray-700 text-gray-400'
                    }`}
                  >
                    <span>{cat}</span>
                    {isSelected && <Check size={14} className="text-[#0FFF55] flex-shrink-0 ml-2" />}
                  </button>
                );
              })}
            </div>
            {errors.categories && <p className="text-xs text-red-500 mt-1">{errors.categories}</p>}
          </div>

          {/* Row 3: Logo & Banner Uploader */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <ImageUploader
                bucketName="logos_fornecedores"
                label="Logo do Fornecedor * (Recomendado: 300x300px, WEBP)"
                value={logoUrl}
                onChange={setLogoUrl}
                options={{ maxWidth: 300, maxHeight: 300, quality: 0.85, format: 'webp' }}
                placeholder="https://exemplo.com/logo.png"
              />
              {errors.logoUrl && <p className="text-xs text-red-500 mt-1">{errors.logoUrl}</p>}
            </div>

            <div>
              <ImageUploader
                bucketName="thumbs_fornecedores"
                label="Banner do Fornecedor * (Recomendado: 1200x400px, WEBP)"
                value={bannerUrl}
                onChange={setBannerUrl}
                options={{ maxWidth: 1200, maxHeight: 400, quality: 0.85, format: 'webp' }}
                placeholder="https://exemplo.com/banner.png"
              />
              {errors.bannerUrl && <p className="text-xs text-red-500 mt-1">{errors.bannerUrl}</p>}
            </div>
          </div>

          {/* Row 4: Numeric Stats */}
          <div className="grid grid-cols-3 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Avaliação * (0-5)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={rating}
                onChange={(e) => setRating(Number(e.target.value))}
                className={`w-full px-4 py-3 bg-[#16191d] border ${
                  errors.rating ? 'border-red-500' : 'border-gray-800 focus:border-[#0FFF55]'
                } rounded-xl text-white text-sm outline-none transition-colors`}
              />
              {errors.rating && <p className="text-xs text-red-500 mt-1">{errors.rating}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Quantidade de Envios *
              </label>
              <input
                type="number"
                value={enviosCount}
                onChange={(e) => setEnviosCount(Number(e.target.value))}
                className={`w-full px-4 py-3 bg-[#16191d] border ${
                  errors.enviosCount ? 'border-red-500' : 'border-gray-800 focus:border-[#0FFF55]'
                } rounded-xl text-white text-sm outline-none transition-colors`}
              />
              {errors.enviosCount && <p className="text-xs text-red-500 mt-1">{errors.enviosCount}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Qtd de Produtos *
              </label>
              <input
                type="number"
                value={produtosCount}
                onChange={(e) => setProdutosCount(Number(e.target.value))}
                className={`w-full px-4 py-3 bg-[#16191d] border ${
                  errors.produtosCount ? 'border-red-500' : 'border-gray-800 focus:border-[#0FFF55]'
                } rounded-xl text-white text-sm outline-none transition-colors`}
              />
              {errors.produtosCount && <p className="text-xs text-red-500 mt-1">{errors.produtosCount}</p>}
            </div>
          </div>

          {/* Redirect / CTA Link */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Link de Ação (Acessar Mastershop) *
            </label>
            <input
              type="text"
              value={redirectUrl}
              onChange={(e) => setRedirectUrl(e.target.value)}
              placeholder="https://mastershop.com/suppliers/..."
              className={`w-full px-4 py-3 bg-[#16191d] border ${
                errors.redirectUrl ? 'border-red-500' : 'border-gray-800 focus:border-[#0FFF55]'
              } rounded-xl text-white text-sm outline-none transition-colors`}
            />
            {errors.redirectUrl && <p className="text-xs text-red-500 mt-1">{errors.redirectUrl}</p>}
          </div>

          {/* Row: WhatsApp, Tempo Médio de Entrega, Criação de Marcas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                WhatsApp do Fornecedor *
              </label>
              <input
                type="text"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="Ex: https://wa.me/5511999999999"
                className={`w-full px-4 py-3 bg-[#16191d] border ${
                  errors.whatsapp ? 'border-red-500' : 'border-gray-800 focus:border-[#0FFF55]'
                } rounded-xl text-white text-sm outline-none transition-colors`}
              />
              {errors.whatsapp && <p className="text-xs text-red-500 mt-1">{errors.whatsapp}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Tempo Médio de Entrega *
              </label>
              <input
                type="text"
                value={tempoMedioEntrega}
                onChange={(e) => setTempoMedioEntrega(e.target.value)}
                placeholder="Ex: 9 dias úteis"
                className={`w-full px-4 py-3 bg-[#16191d] border ${
                  errors.tempoMedioEntrega ? 'border-red-500' : 'border-gray-800 focus:border-[#0FFF55]'
                } rounded-xl text-white text-sm outline-none transition-colors`}
              />
              {errors.tempoMedioEntrega && <p className="text-xs text-red-500 mt-1">{errors.tempoMedioEntrega}</p>}
            </div>

            <div className="flex flex-col justify-center">
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Criação de Marca Própria (Private Label)
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasPrivateLabel}
                  onChange={(e) => setHasPrivateLabel(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-400 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0FFF55] peer-checked:after:bg-black peer-checked:after:border-transparent"></div>
                <span className="ml-3 text-sm text-gray-300">Sim</span>
              </label>
            </div>
          </div>

          {/* Descriptions */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Descrição Curta * (Exibida no Card)
            </label>
            <textarea
              value={descriptionShort}
              onChange={(e) => setDescriptionShort(e.target.value)}
              maxLength={150}
              rows={2}
              placeholder="Digite um resumo curto (máx. 150 caracteres)..."
              className={`w-full px-4 py-3 bg-[#16191d] border ${
                errors.descriptionShort ? 'border-red-500' : 'border-gray-800 focus:border-[#0FFF55]'
              } rounded-xl text-white text-sm outline-none transition-colors resize-none`}
            />
            {errors.descriptionShort && <p className="text-xs text-red-500 mt-1">{errors.descriptionShort}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Descrição Completa * (Exibida nos Detalhes)
            </label>
            <textarea
              value={descriptionFull}
              onChange={(e) => setDescriptionFull(e.target.value)}
              rows={4}
              placeholder="Forneça detalhes completos da parceria, prazos, vantagens..."
              className={`w-full px-4 py-3 bg-[#16191d] border ${
                errors.descriptionFull ? 'border-red-500' : 'border-gray-800 focus:border-[#0FFF55]'
              } rounded-xl text-white text-sm outline-none transition-colors resize-y`}
            />
            {errors.descriptionFull && <p className="text-xs text-red-500 mt-1">{errors.descriptionFull}</p>}
          </div>

          {/* Integration Guide / Steps */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Como hospedar minha marca com este fornecedor? (Instruções Passo a Passo)
            </label>
            <textarea
              value={hospedarPassos}
              onChange={(e) => setHospedarPassos(e.target.value)}
              rows={4}
              placeholder="1. Faça o cadastro na plataforma...&#10;2. Envie o formulário de integração...&#10;3. Aguarde o contato..."
              className={`w-full px-4 py-3 bg-[#16191d] border ${
                errors.hospedarPassos ? 'border-red-500' : 'border-gray-800 focus:border-[#0FFF55]'
              } rounded-xl text-white text-sm outline-none transition-colors resize-y`}
            />
            {errors.hospedarPassos && <p className="text-xs text-red-500 mt-1">{errors.hospedarPassos}</p>}
          </div>

          {/* Footer actions inside form */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-transparent text-gray-400 hover:text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-[#0FFF55] hover:bg-[#0dd149] text-black text-sm font-bold rounded-xl transition-all"
            >
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierForm;
